import moment from 'moment';
import dataProvider from '../../dataProvider';
import {
  convertTime,
  formatScheduleTypeName,
  getZ3PConfigValue,
  perPage,
} from '../../helper';
import { generateTimes } from './availibilityFieldHelpers';

export const fetchRecurringScheduleDetails = (
  serviceVehicle,
  scheduleInstanceData,
) => {
  const { id: serviceVehicleId } = serviceVehicle;
  const defaultStartTime =
    `${convertTime(
      getZ3PConfigValue('time_range_min'),
      'militaryString',
    )}:00` || '09:00:00';
  const defaultEndTime =
    `${convertTime(
      getZ3PConfigValue('time_range_max'),
      'militaryString',
    )}:00` || '17:00:00';
  const daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const defaultTimeBlocks = generateTimes(
    defaultStartTime,
    defaultEndTime,
    1,
  ).map((entries) => entries.time);
  return dataProvider
    .getList('recurring-schedule', {
      filter: {
        is_canceled: false,
        frequency: 'weekly',
        service_vehicle_id: serviceVehicleId,
      },
      sort: { field: 'recurring_schedule_id', order: 'ASC' },
      pagination: { page: 1, perPage },
    })
    .then(({ data: recurringSchedules }) => {
      const currentRecurringSchedules = recurringSchedules.filter(
        (rs) => rs.end_date >= moment().format('YYYY-MM-DD'),
      );
      return daysOfWeek.map((day) => {
        // Multiple active recurring schedules will be rare, but sort to select the most recent recurring schedule
        const matchingSchedule = currentRecurringSchedules
          .filter((schedule) => schedule.day_of_week === day)
          .sort((a, b) => {
            return moment(b.created_at) - moment(a.created_at);
          })?.[0];
        if (matchingSchedule) {
          const associatedScheduleInstance = scheduleInstanceData.find(
            (si) =>
              si.recurring_schedule_id ===
                matchingSchedule.recurring_schedule_id &&
              !si.is_exception_to_recurring_schedule &&
              // use same logic that updates recurring schedule instances in brain2
              si.start_time >= moment().format('YYYY-MM-DD'),
          );
          return {
            recurring_schedule_id: matchingSchedule.recurring_schedule_id,
            service_vehicle_id: serviceVehicleId,
            start_time: matchingSchedule.start_time,
            end_time: matchingSchedule.end_time,
            time_zone: matchingSchedule.time_zone,
            predetermined_slot_times: matchingSchedule.predetermined_slot_times,
            day_of_week: day,
            is_canceled: matchingSchedule.is_canceled,
            internal_note: matchingSchedule.internal_note,
            limit_scheduled_works: matchingSchedule.limit_scheduled_works,
            limit_total_hours: matchingSchedule.limit_total_hours,
            limit_total_driving_hours:
              matchingSchedule.limit_total_driving_hours,
            offered_service_types: associatedScheduleInstance
              ? associatedScheduleInstance.offered_service_types
              : [],
            client_location_ids: associatedScheduleInstance
              ? associatedScheduleInstance.client_locations.map(
                  (cl) => cl.client_location_id,
                )
              : [],
            schedule_dates_with_active_sws:
              matchingSchedule.schedule_dates_with_active_sws,
            is_changed: false,
            is_new: false,
            toggles: {
              // Expand by default if schedule types are missing
              isExpanded:
                associatedScheduleInstance?.offered_service_types?.length === 0,
              enableAppointmentLimit: !!matchingSchedule.limit_scheduled_works,
              enableDriveTimeLimit: !!matchingSchedule.limit_total_driving_hours,
            },
          };
        }
        const defaultScheduleTypes = serviceVehicle.default_schedule_types;
        return {
          service_vehicle_id: serviceVehicleId,
          start_date: moment().format('YYYY-MM-DD'),
          // By default, create 3 years of schedule instances
          end_date: moment()
            .add(3, 'years')
            .format('YYYY-MM-DD'),
          start_time: defaultStartTime,
          end_time: defaultEndTime,
          predetermined_slot_times: defaultTimeBlocks,
          time_zone: 'America/New_York',
          day_of_week: day,
          offered_service_types: defaultScheduleTypes,
          client_location_ids: [],
          is_canceled: true,
          is_changed: false,
          is_new: true,
          toggles: {
            isExpanded: false,
            enableAppointmentLimit: false,
            enableDriveTimeLimit: false,
          },
        };
      });
    });
};

export const getAllScheduleTypes = (translate) => {
  return dataProvider
    .getList('schedule-type', {
      filter: {},
      sort: { field: 'name', order: 'ASC' },
      pagination: { page: 1, perPage: 100 },
    })
    .then((response) => {
      const menuOptions = [];
      if (response?.data) {
        response.data.forEach((item) => {
          return menuOptions.push({
            id: item.name,
            name: formatScheduleTypeName(item.name, translate),
          });
        });
      }
      return menuOptions;
    });
};

export const getAllClientLocations = () => {
  return dataProvider
    .getList('client-location', {
      filter: { is_active: true },
      sort: { field: 'short_name', order: 'ASC' },
      pagination: { page: 1, perPage: 100 },
    })
    .then((response) => {
      const locationOptions = [];
      if (response && response.data) {
        response.data.forEach((item) =>
          locationOptions.push({
            id: item.id,
            short_name: item.short_name,
          }),
        );
      }
      return locationOptions;
    });
};

export const getScheduleStats = (serviceVehicleId, start, end) => {
  return dataProvider
    .getList('schedule/stats', {
      filter: {
        service_vehicle_id: serviceVehicleId,
        start_time_after: moment(start).format(),
        end_time_before: moment(end).format(),
      },
      sort: { field: 'date', order: 'ASC' },
      pagination: { page: 1, perPage: 1000 },
    })
    .then((response) => response?.data || []);
};
